import React, { useEffect, useMemo, useRef, useState } from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { Button } from '../../element/Button/Button';
import { LayoutAnimation } from '../../../utils/layout-animation';

import { ResizableColumnsContext } from './ResizableColumnsContext';
import type { ResizableColumnsItemProps } from './ResizableColumnsItem';
import { useResizableColumnsDrag } from './useResizableColumnsDrag';

/* The divider's track. A drag target rather than a design value, so it is the same everywhere. */
const HANDLE_WIDTH = '8px';

/**
 * A column's width: a number is a share of what the run has to divide - `[2, 9, 1]` reads as 2:9:1 -
 * and a `px` string is a fixed track that keeps its width whatever the container does. Mixing them
 * is the point: a fixed sidebar beside columns that divide the rest.
 */
export type ResizableColumnsWidth = number | `${number}px`;

export type ResizableColumnsProps = {
  /**
   * Eases the columns between one set of `widths` and the next, rather than snapping. For a layout
   * whose widths are driven by something the reader did elsewhere - a drawer opening, a panel being
   * dismissed - where a jump reads as a glitch rather than as a result.
   *
   * A drag is never eased, whatever this says: the columns have to stay under the pointer.
   */
  animated?: boolean;
  /** One `ResizableColumnsItem` per column. Two is the minimum; twelve matches `Grid`. */
  children: React.ReactNode;
  /**
   * How long `animated` takes. Short by default, and that is not a taste call: anything sized as a
   * percentage *of* one of these columns animates against a containing block that is itself moving,
   * so the two compose and the longer the columns take, the further such a thing drifts from
   * whatever it was keeping pace with. Reach for a longer value only when nothing is.
   *
   * Nothing to do with a drag, which is never eased.
   */
  duration?: `${number}s` | `${number}ms`;
  name?: string;
  /**
   * The widths after a drag, once the reader lets go. Where a caller would persist a layout; it is
   * not called while the drag is in flight.
   */
  onWidthsChange?: (widths: ResizableColumnsWidth[]) => void;
  /**
   * Hands sizing to the reader: dividers appear between the columns and can be dragged, freely and
   * as far as collapsing a column to nothing.
   *
   * **This is the toggle between the two ways of sizing.** While it is on, what the reader dragged
   * wins and `widths` is ignored; while it is off, `widths` is what shows. Turning it off returns
   * the columns to `widths`, and turning it back on returns them to where the reader left them.
   *
   * A pair with a fixed column in it gets no handle: there is no share to move between them.
   */
  resizable?: boolean;
  /**
   * `divider` draws a line between the columns. On the group rather than on a column, because a
   * line *between* things belongs to the run: the outer edge is not drawn, and neither is a line
   * anywhere `resizable` has already put a handle - a divider and a draggable divider are the same
   * statement twice.
   */
  variation?: 'none' | 'divider';
  /**
   * Width per column, in the order the columns appear - a share, or a `px` string for a track that
   * does not divide anything. Equal shares if omitted.
   *
   * Ignored while `resizable` is on, once the reader has dragged. Widths they dragged arrive back
   * through `onWidthsChange`, so a caller holding this in state stays in step across the toggle.
   */
  widths?: ResizableColumnsWidth[];
};

/**
 * Columns that fill the height they are given, scroll their own overflow, and can be resized -
 * either by the code that holds them or by the reader dragging the dividers.
 *
 * **It fills its parent, so its parent has to have a height.** `height: 100%` against an auto-height
 * ancestor resolves to nothing and the columns get none at all - give the container a height, the
 * way a page's shell does.
 *
 * **Why it exists alongside `Grid` and `Layout`.** Semantic's grid wraps, so its line takes the
 * height of its tallest content and grows past whatever it was given, and a line that grows cannot
 * hold a scroller. `Layout` can hold one but has no divider to drag. This is a CSS grid, which is
 * single-line by definition, so each column is exactly the height of its track and scrolls inside
 * it.
 *
 * Columns are children, so what goes in them is the caller's. Two is the minimum for a divider to
 * mean anything, and twelve is the most the design system's grid describes.
 */
export const ResizableColumns = ({
  animated = false,
  children,
  duration,
  name,
  onWidthsChange,
  resizable = false,
  variation = 'none',
  widths,
}: ResizableColumnsProps) => {
  const [draggedWidths, setDraggedWidths] = useState<
    ResizableColumnsWidth[] | null
  >(null);
  const rootRef = useRef<HTMLDivElement>(null);

  /*
    Publishes the element rather than the name, because the name cannot answer the question a
    consumer is asking: transition events bubble, so telling this group's animation from a column's
    own takes the element the transition is declared on. Registered whether or not `animated` is on -
    a group that is not easing simply never fires, and the prop can be turned on later.
  */
  useEffect(() => {
    const element = rootRef.current;

    if (!element) return;

    /*
      The track list is what this group eases, so it is the property that reports - and unlike a
      custom property transitioned purely to have something to listen to, it also covers a partial
      state, which changes a width like any other.
    */
    LayoutAnimation.register({
      element,
      name,
      property: 'grid-template-columns',
    });

    return () => {
      LayoutAnimation.unregister({ name });
    };
  }, [name]);

  const columns = React.Children.toArray(children).filter(
    (child): child is React.ReactElement<ResizableColumnsItemProps> =>
      React.isValidElement(child)
  );

  /*
    **A caller error, and a loud one.** There is nothing sensible to reconcile: one entry too many
    renders a draggable divider after the last column that can never work, plus an empty track past
    it, and one too few drops a column into an implicit `auto` track. Thrown rather than trimmed,
    the way `Brief` throws for its own impossible prop combination.
  */
  if (widths && widths.length !== columns.length) {
    throw new Error(
      `ResizableColumns was given ${widths.length} widths for ${columns.length} columns. Pass one width per column, or none at all.`
    );
  }

  const givenWidths = widths ?? columns.map(() => 1);

  /*
    What the reader dragged, but only while they are the one sizing it - and only while it still
    describes the columns that are actually there. The length check is for a caller that adds or
    removes a column with a dragged layout held: the stale array would silently mis-size the run.
  */
  const currentWidths =
    resizable && draggedWidths?.length === columns.length
      ? draggedWidths
      : givenWidths;

  /*
    The group's clock, read by a column rather than pushed into it: a column always sits inside its
    group, so there is nothing to thread and no need to reach into children that are the caller's.
  */
  const clock = useMemo(() => ({ duration }), [duration]);

  const { draggingIndex, nudge, startDrag } = useResizableColumnsDrag({
    onChange: setDraggedWidths,
    onCommit: nextWidths => {
      onWidthsChange?.(nextWidths);
    },
    widths: currentWidths,
  });

  /*
    **A column no drag can reach**: one measured in pixels, which has no share to give, or one whose
    caller said so outright. Read off the child rather than tracked here, so a column and its own
    prop cannot fall out of step.
  */
  const isFixed = (index: number) =>
    typeof currentWidths[index] !== 'number' ||
    columns[index]?.props.disableResize === true;

  /*
    **A divider only between two columns that can both move.** A drag takes from one and gives to
    the other, so a handle beside a fixed column would be a control that does nothing.
  */
  const hasHandle = (index: number) =>
    resizable &&
    index < currentWidths.length - 1 &&
    !isFixed(index) &&
    !isFixed(index + 1);

  /*
    **One track per column, one per divider between them.** `minmax(0, …)` rather than the width on
    its own: a track's automatic minimum is its content, so both `fr` and `px` refuse to shrink past
    whatever is inside them - which would stop a column collapsing and stop it scrolling. The `0`
    floor is what allows both.
  */
  const template = currentWidths
    .flatMap((width, index) => {
      const track = `minmax(0, ${typeof width === 'number' ? `${width}fr` : width})`;

      return hasHandle(index) ? [track, HANDLE_WIDTH] : [track];
    })
    .join(' ');

  return (
    <div
      className={AttrsHelper.formatClassname(
        'resizable-columns',
        animated && 'resizable-columns-animated',
        variation === 'divider' && 'resizable-columns-divider'
      )}
      data-test={name}
      ref={rootRef}
      style={{ gridTemplateColumns: template, transitionDuration: duration }}
    >
      <ResizableColumnsContext.Provider value={clock}>
        {columns.map((column, index) => (
          <React.Fragment key={column.key}>
            {column}
            {hasHandle(index) ? (
              /*
                **A `Button`, styled down to a line.** The divider has to be focusable to be usable
                without a pointer, and the two ways of doing that by hand are both closed: a raw
                `button` is forbidden here, and a `div` carrying `tabIndex` is a non-interactive
                element pretending to be one. `Button` is the design system's own, so the focus and
                the keyboard come with it - `minimal` is what leaves it with no surface to undo.

                The divider is a grid track of its own rather than an edge on a column: a track
                keeps its width when the column beside it has been dragged to nothing, which is the
                only thing that makes a collapsed column recoverable.
              */
              <Button
                className={AttrsHelper.formatClassname(
                  'resizable-columns-handle',
                  draggingIndex === index && 'resizable-columns-handle-dragging'
                )}
                name={name ? `${name}_HANDLE_${index}` : undefined}
                onKeyDown={event => {
                  if (event.key === 'ArrowLeft')
                    nudge({ direction: -1, index });
                  if (event.key === 'ArrowRight')
                    nudge({ direction: 1, index });
                }}
                onPointerDown={event => {
                  startDrag({ event, index });
                }}
                variation="minimal"
              />
            ) : null}
          </React.Fragment>
        ))}
      </ResizableColumnsContext.Provider>
    </div>
  );
};
